import { createReducer } from "typesafe-actions";

import { Group } from "@mapmycustomers/shared/types/entity";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import {
  Actions,
  bulkDeleteGroups,
  bulkUpdateGroups,
  bulkUpdateGroupSharing,
  createGroup,
  deleteGroup,
  fetchAllGroups,
  fetchGroups,
  updateEntities,
  updateGroup,
  updateGroupSharing,
} from "./actions";
import GroupsState from "./GroupsState";
import { stateFieldBaseName, stateFieldBaseNameForGroupEntity } from "./util";

const initialState: GroupsState = {
  companyGroups: [],
  companyGroupsError: undefined,
  companyGroupsLoading: false,
  createGroupError: undefined,
  createGroupLoading: false,
  dealGroups: [],
  dealGroupsError: undefined,
  dealGroupsLoading: false,
  deleteGroupLoading: false,
  groupsBulkUpdating: false,
  peopleGroups: [],
  peopleGroupsError: undefined,
  peopleGroupsLoading: false,
  updateEntitiesLoading: false,
  updateGroupLoading: false,
};

const groups = createReducer<GroupsState, Actions>(initialState)
  .handleAction(fetchGroups.request, (state, { payload: { entityType } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}Error`]: undefined,
    [`${stateFieldBaseName[entityType]}Loading`]: true,
  }))
  .handleAction(fetchGroups.success, (state, { payload: { entityType, groups } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}`]: groups,
    [`${stateFieldBaseName[entityType]}Loading`]: false,
  }))
  .handleAction(fetchGroups.failure, (state, { payload: { entityType, error } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}Error`]: error,
    [`${stateFieldBaseName[entityType]}Loading`]: false,
  }))
  .handleAction(fetchAllGroups.success, (state, { payload }) => {
    return (Object.keys(payload) as Group["entity"][]).reduce((result, entityType) => {
      return {
        ...result,
        [`${stateFieldBaseNameForGroupEntity[entityType]}`]: payload[entityType],
      };
    }, state);
  })
  .handleAction(createGroup.request, (state) => ({
    ...state,
    createGroupError: undefined,
    createGroupLoading: true,
  }))
  .handleAction(createGroup.success, (state, { payload: { entityType, group } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}`]: [...state[stateFieldBaseName[entityType]], group].sort(
      nameComparator
    ),
    createGroupLoading: false,
  }))
  .handleAction(createGroup.failure, (state, action) => ({
    ...state,
    createGroupError: action.payload,
    createGroupLoading: false,
  }))
  .handleAction([updateGroup.request, updateGroupSharing.request], (state) => ({
    ...state,
    updateGroupLoading: true,
  }))
  .handleAction(
    [updateGroup.success, updateGroupSharing.success],
    (state, { payload: { entityType, group } }) => ({
      ...state,
      [`${stateFieldBaseName[entityType]}`]: state[stateFieldBaseName[entityType]].map((item) =>
        item.id !== group.id ? item : group
      ),
      updateGroupLoading: false,
    })
  )
  .handleAction([updateGroup.failure, updateGroupSharing.failure], (state) => ({
    ...state,
    updateGroupLoading: false,
  }))
  .handleAction(updateEntities.request, (state) => ({
    ...state,
    updateEntitiesLoading: true,
  }))
  .handleAction([updateEntities.success, updateEntities.failure], (state) => ({
    ...state,
    updateEntitiesLoading: false,
  }))
  .handleAction(deleteGroup.request, (state) => ({ ...state, deleteGroupLoading: true }))
  .handleAction(deleteGroup.success, (state, { payload: { entityType, groupId } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}`]: state[stateFieldBaseName[entityType]].filter(
      ({ id }) => id !== groupId
    ),
    deleteGroupLoading: false,
  }))
  .handleAction(deleteGroup.failure, (state) => ({ ...state, deleteGroupLoading: false }))
  .handleAction(bulkDeleteGroups.request, (state) => ({ ...state, groupsBulkUpdating: true }))
  .handleAction(bulkDeleteGroups.success, (state, { payload: { entityType, groupIds } }) => ({
    ...state,
    [`${stateFieldBaseName[entityType]}`]: state[stateFieldBaseName[entityType]].filter(
      ({ id }) => !groupIds.includes(id)
    ),
    groupsBulkUpdating: false,
  }))
  .handleAction(bulkDeleteGroups.failure, (state) => ({ ...state, groupsBulkUpdating: false }))
  .handleAction(bulkUpdateGroups.request, (state) => ({
    ...state,
    groupsBulkUpdating: true,
  }))
  .handleAction([bulkUpdateGroups.success, bulkUpdateGroups.failure], (state) => ({
    ...state,
    groupsBulkUpdating: false,
  }))
  .handleAction(bulkUpdateGroupSharing.request, (state) => ({
    ...state,
    groupsBulkUpdating: true,
  }))
  .handleAction(
    bulkUpdateGroupSharing.success,
    (state, { payload: { entityType, groupIds, userIds } }) => ({
      ...state,
      // There's a timeout after shared-groups api requests and the moment group is actually updated
      // so we're using a workaround for now. For more info: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1663156032349549
      [`${stateFieldBaseName[entityType]}`]: state[stateFieldBaseName[entityType]].map((group) =>
        groupIds.includes(group.id)
          ? {
              ...group,
              userIds,
            }
          : group
      ),
      groupsBulkUpdating: false,
    })
  )
  .handleAction(bulkUpdateGroupSharing.failure, (state) => ({
    ...state,
    groupsBulkUpdating: false,
  }));
export * from "./selectors";
export type GroupsActions = Actions;
export default groups;
